<template>
  <div class="product-selector">
    <!-- 商品卡片 -->
    <div v-for="(item, index) in productList" :key="item.uid" class="product-card">
      <div class="card-header">
        <span>商品 {{ index + 1 }}</span>
        <el-button v-if="productList.length > 1" type="danger" link @click="removeProduct(index)">删除</el-button>
      </div>

      <!-- 图片上传 / 回显 -->
      <el-form-item label="商品图" required>
        <CUpload
          list-type="image"
          src-type="url"
          accept="image/*"
          :multiple="false"
          @completed="(t: any)=>item.headImage = t.url"
        />
        <span class="tip">建议图片宽750px，高578px</span>
      </el-form-item>

      <!-- 商品选择 -->
      <el-form-item label="选择商品" required>
        <el-input v-model="item.spuName" placeholder="请选择商品" readonly @click="openDialog(index)" />
      </el-form-item>
    </div>

    <!-- 新增商品 -->
    <el-button type="primary" plain :disabled="productList.length >= MAX_COUNT" @click="addProduct">新增商品</el-button>
    <span v-if="productList.length >= MAX_COUNT" class="limit-tip">最多选择 5 个商品</span>

    <!-- 选择商品弹窗 -->
    <el-dialog v-model="dialogVisible" title="选择商品" width="70%">
      <!-- 搜索 -->
      <el-form :inline="true" @submit.prevent>
        <el-form-item label="商品名称">
          <el-input v-model="query.spuName" placeholder="请输入商品名称" clearable />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" @click="search">搜索</el-button>
          <el-button @click="reset">重置</el-button>
        </el-form-item>
      </el-form>

      <!-- 表格 -->
      <el-table :data="spuList" highlight-current-row>
        <el-table-column prop="spuCode" label="SPU Code" width="180" />
        <el-table-column prop="spuName" label="商品名称" />
        <el-table-column label="操作" width="120" align="center">
          <template #default="{ row }">
            <el-button type="primary" size="small" :disabled="isSelected(row.spuCode)" @click="selectSpu(row)">
              {{ isSelected(row.spuCode) ? '已选择' : '选择' }}
            </el-button>
          </template>
        </el-table-column>
      </el-table>
      <span v-if="!hasNextPage" class="no-more">没有更多数据了</span>

      <!-- 分页 -->
      <div class="pager">
        <el-pagination
          layout="prev, next"
          :current-page="query.pageIndex"
          :page-size="query.pageSize"
          :disabled="!hasNextPage && query.pageIndex === 1"
          @current-change="changePage"
        />
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, watch, computed } from 'vue'

const props = withDefaults(
  defineProps<{
    model: Record<string, any>
    prop: string
    hidden?: boolean
    disabled?: boolean
    labelProp: string
  }>(),
  {
    hidden: false,
    disabled: false
  }
)
import { onMounted, useCurrentAppInstance } from '@zto/zpage'
import { deepClone } from '@zto/zpage-core/utils/lodash'
import { CUpload } from '@zto/zpage-ui-element/components'

const app = useCurrentAppInstance()
const MAX_COUNT = 5
const hasNextPage = ref(true)

const emit = defineEmits(['update:modelValue'])

/* 商品结构 */
const createItem = () => ({
  uid: Date.now() + Math.random(),
  imageFile: null,
  imageUrl: '',
  spuCode: '',
  spuName: ''
})

/* 商品列表 */
// const productList = ref(
//   props.modelValue.length ? props.modelValue : [createItem()]
// )
const productList = computed({
  get() {
    return innerValue.value?.length ? innerValue.value : [createItem()]
  },
  set(val) {
    innerValue.value = val
  }
})

/* 弹窗 */
const dialogVisible = ref(false)
const currentIndex = ref(0)

/* 查询参数 */
const query = ref({
  spuName: '',
  pageIndex: 1,
  pageSize: 10
})

const spuList = ref([])
const total = ref(0)

const innerValue = computed<any[]>({
  get() {
    return props.model[props.labelProp]
  },
  set(val) {
    // v-model
    // emit('update:modelValue', val)

    // 同步到外层 form（你现在用的方式）
    if (props.labelProp && props.model) {
      props.model[props.labelProp] = val
    }
  }
})

onMounted(() => {
  if (props.model?.spuGoodsList && props.model.spuGoodsList.length) {
    innerValue.value = props.model.spuGoodsList
  }
})

/* 同步父组件 */
// watch(
//   productList,
//   val => {
//     // debugger
//     // emit('update:modelValue', val)
//
//   },
//   { deep: true }
// )

/* 已选 spuCode */
const selectedCodes = computed(() => productList.value.map(i => i.spuCode).filter(Boolean))

/* 是否已选 */
const isSelected = spuCode => selectedCodes.value.includes(spuCode)

/* 新增 / 删除 */
const addProduct = () => {
  if (productList.value.length >= MAX_COUNT) return
  const productListAdd = deepClone(productList.value)
  productListAdd.push(createItem())
  props.model[props.labelProp] = productListAdd
}

const removeProduct = index => {
  const productListRemove = deepClone(productList.value)
  productListRemove.splice(index, 1)
  props.model[props.labelProp] = productListRemove
}

/* 图片上传 & 回显 */
const onImageChange = (file, index) => {
  const raw = file.raw
  productList.value[index].imageFile = raw
  productList.value[index].imageUrl = URL.createObjectURL(raw)
}

/* 打开弹窗 */
const openDialog = async index => {
  currentIndex.value = index
  dialogVisible.value = true
  await fetchSpuList()
}

/* 查询商品 */
const fetchSpuList = async () => {
  const res = await app.request({
    api: 'pc/goods/querySpuInfoList',
    params: query.value
  })

  const list = res || []

  spuList.value = list
  hasNextPage.value = list.length === query.value.pageSize
}

/* 选择商品 */
const selectSpu = row => {
  const item = productList.value[currentIndex.value]
  item.spuCode = row.spuCode
  item.spuName = row.spuName
  dialogVisible.value = false
}

/* 搜索 / 重置 */
const search = () => {
  query.value.pageIndex = 1
  fetchSpuList()
}

const reset = () => {
  query.value.spuName = ''
  query.value.pageIndex = 1
  fetchSpuList()
}

/* 分页 */
const changePage = page => {
  // 点下一页但已无数据
  if (page > query.value.pageIndex && !hasNextPage.value) return

  // 点上一页但已经是第一页
  if (page < 1) return

  query.value.pageIndex = page
  fetchSpuList()
}

const changeSize = size => {
  query.value.pageSize = size
  query.value.pageIndex = 1
  fetchSpuList()
}
</script>

<style scoped>
.product-card {
  border: 1px solid #ebeef5;
  border-radius: 8px;
  padding: 16px 16px 4px;
  margin-bottom: 16px;
  background: #fff;
}

.card-header {
  display: flex;
  justify-content: space-between;
  margin-bottom: 12px;
  font-weight: 500;
}

.image-uploader {
  display: inline-block;
}

.image-preview {
  width: 120px;
  height: 92px;
  object-fit: cover;
  border-radius: 6px;
  border: 1px solid #ebeef5;
}

.tip {
  margin-left: 12px;
  font-size: 12px;
  color: #999;
}

.limit-tip {
  margin-left: 12px;
  font-size: 12px;
  color: #f56c6c;
}

.pager {
  margin-top: 16px;
  text-align: right;
}

.no-more {
  margin-left: 12px;
  color: #999;
  font-size: 12px;
}
</style>
